import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, subDays } from 'date-fns';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { BarChart3, RefreshCw, LogIn, Users, CalendarRange } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { analyticsAPI } from '../services/api';

const toInputDate = (d) => format(d, 'yyyy-MM-dd');

export default function LoginAnalytics() {
  const defaultTo = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => subDays(defaultTo, 30), [defaultTo]);

  const [from, setFrom] = useState(() => toInputDate(defaultFrom));
  const [to, setTo] = useState(() => toInputDate(defaultTo));
  const [granularity, setGranularity] = useState('day');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await analyticsAPI.getLoginStats({ from, to, granularity });
      if (!res.success) {
        setError(res.message || 'Failed to load analytics');
        setPayload(null);
        return;
      }
      setPayload(res.data);
    } catch (e) {
      setError(e.message || 'Failed to load analytics');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, granularity]);

  useEffect(() => {
    load();
  }, [load]);

  const chartRows = useMemo(() => {
    if (!payload?.timeline?.length) return [];
    return payload.timeline.map((row) => ({
      label: format(new Date(row.bucket), granularity === 'month' ? 'MMM yyyy' : 'dd MMM'),
      logins: row.login_count,
      uniqueUsers: row.unique_users,
    }));
  }, [payload, granularity]);

  const clientLabel = (t) => {
    if (t === 'web') return 'Web';
    if (t === 'mobile') return 'Mobile app';
    return 'Unknown';
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-green-600" />
              Login analytics
            </h1>
            <p className="text-ga-text-muted mt-1">
              Track partner dashboard login activity over time. Super Admin only.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 ga-btn-primary px-4 py-2 rounded-lg disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="ga-card rounded-xl shadow-sm border p-4 flex flex-col lg:flex-row gap-4 flex-wrap">
          <div>
            <label className="block text-xs font-medium text-ga-text-muted mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="ga-input rounded-lg py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ga-text-muted mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="ga-input rounded-lg py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ga-text-muted mb-1">Group by</label>
            <select
              value={granularity}
              onChange={(e) => setGranularity(e.target.value)}
              className="ga-input rounded-lg py-2 min-w-[140px]"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm">{error}</div>
        )}

        {loading && !payload ? (
          <div className="py-16 text-center text-ga-text-muted">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="ga-card rounded-xl border p-4 stat-card-blue flex items-center gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                  <LogIn className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-ga-text-muted uppercase tracking-wide">Total logins</p>
                  <p className="text-2xl font-bold text-ga-text-primary">
                    {payload?.summary?.total_logins ?? 0}
                  </p>
                </div>
              </div>
              <div className="ga-card rounded-xl border p-4 stat-card-green flex items-center gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-50">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-ga-text-muted uppercase tracking-wide">Unique users</p>
                  <p className="text-2xl font-bold text-ga-text-primary">
                    {payload?.summary?.unique_users ?? 0}
                  </p>
                </div>
              </div>
              <div className="ga-card rounded-xl border p-4 stat-card-purple flex items-center gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-purple-50">
                  <CalendarRange className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-medium text-ga-text-muted uppercase tracking-wide">Date range</p>
                  <p className="text-sm font-semibold text-ga-text-primary">
                    {payload?.range?.from?.slice(0, 10)} → {payload?.range?.to?.slice(0, 10)}
                  </p>
                </div>
              </div>
            </div>

            <div className="ga-card rounded-xl border p-4">
              <h2 className="text-lg font-semibold text-ga-text-primary mb-4">Timeline</h2>
              {chartRows.length === 0 ? (
                <p className="text-ga-text-muted text-sm">No login events in this range yet.</p>
              ) : (
                <div className="h-80 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartRows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradLogins" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#16a34a" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                          <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="logins" name="Logins" stroke="#16a34a" strokeWidth={2} fill="url(#gradLogins)" dot={false} />
                      <Area type="monotone" dataKey="uniqueUsers" name="Unique users" stroke="#2563eb" strokeWidth={2} fill="url(#gradUsers)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="ga-card rounded-xl border p-4">
                <h2 className="text-lg font-semibold text-ga-text-primary mb-3">By client</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-ga-text-muted border-b border-ga-border-subtle">
                        <th className="pb-2 font-medium">Channel</th>
                        <th className="pb-2 font-medium">Logins</th>
                        <th className="pb-2 font-medium">Unique users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(payload?.by_client || []).map((row) => (
                        <tr key={row.client_type} className="border-b border-ga-border-subtle/50">
                          <td className="py-2">{clientLabel(row.client_type)}</td>
                          <td className="py-2">{row.login_count}</td>
                          <td className="py-2">{row.unique_users}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!payload?.by_client || payload.by_client.length === 0) && (
                    <p className="text-ga-text-muted text-sm py-4">No data.</p>
                  )}
                </div>
              </div>

              <div className="ga-card rounded-xl border p-4">
                <h2 className="text-lg font-semibold text-ga-text-primary mb-3">Recent logins (up to 100)</h2>
                <div className="overflow-x-auto max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-ga-bg-surface">
                      <tr className="text-left text-ga-text-muted border-b border-ga-border-subtle">
                        <th className="pb-2 font-medium">When</th>
                        <th className="pb-2 font-medium">User</th>
                        <th className="pb-2 font-medium">Channel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(payload?.recent || []).map((row, idx) => (
                        <tr key={`${row.logged_in_at}-${idx}`} className="border-b border-ga-border-subtle/50">
                          <td className="py-2 whitespace-nowrap">
                            {row.logged_in_at
                              ? format(new Date(row.logged_in_at), 'dd MMM yyyy HH:mm')
                              : '—'}
                          </td>
                          <td className="py-2">
                            <span className="font-medium text-ga-text-primary">{row.name || row.username}</span>
                            <span className="text-ga-text-muted text-xs block">@{row.username}</span>
                          </td>
                          <td className="py-2">{clientLabel(row.client_type)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(!payload?.recent || payload.recent.length === 0) && (
                    <p className="text-ga-text-muted text-sm py-4">No events.</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
